import PropTypes from 'prop-types';
import { Box, CircularProgress, Grid, Link, useTheme } from '@mui/material';
import Container from '../../layout/Container';
import { usePage } from '@inertiajs/react';
import { CloseIcon } from '../../assets/icons/icons';
import { forwardRef, useEffect, useState } from 'react';
import Button from '../Button';
import SearchBar from '../SearchBar';
import Column from './Column';
import Products from './Products';
import ProductCategories from './ProductCategories';
import Resources from './Resources';
import LinkButton from '../../components/LinkButton';

const SearchModal = forwardRef(
  ({ isOpen, onClose, onClickUpdatesToSearch, loading: loadingProp }, ref) => {
    const theme = useTheme();

    const [loading, setLoading] = useState(loadingProp);

    useEffect(() => {
      setLoading(loadingProp);
    }, [loadingProp]);

    // close on Esc press
    useEffect(() => {
      const closeModalOnEsc = (e) => {
        if (isOpen && e.key === 'Escape') {
          onClose(false);
        }
      };
      document.addEventListener('keydown', closeModalOnEsc);

      return () => document.removeEventListener('keydown', closeModalOnEsc);
    }, [isOpen]);

    const {
      props: {
        search_results: {
          is_default: isDefault,
          product_categories: productCategories,
          products,
          resources,
          viewAllUrl,
        },
      },
    } = usePage();

    return (
      <Box
        data-testid="search-modal"
        ref={ref}
        display={isOpen ? 'flex' : 'none'}
        flexDirection="column"
        position="absolute"
        top={{ xs: 81, md: 88 }}
        left={0}
        width="100%"
        bgcolor="common.white"
        boxShadow={theme.shadows[10]}
        zIndex={theme.zIndex.modal}
        color="gray.900"
      >
        <Box ml="auto" pt={2} pr={2}>
          <Button onClick={onClose} aria-label="Close search menu">
            <CloseIcon />
          </Button>
        </Box>

        <Container>
          <Box display={{ xs: 'block', md: 'none' }}>
            <SearchBar
              isInModal
              onSearchLoading={(newSearchLoading) => setLoading(newSearchLoading)}
            />
          </Box>

          <Box display={{ xs: 'block', md: 'none' }} mb={4}>
            <LinkButton onClick={onClickUpdatesToSearch}>Updates to Search</LinkButton>
          </Box>

          <Grid
            container
            mt={{ xs: 1, md: 6 }}
            mb={{ xs: 4, md: 15 }}
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            columnSpacing={3}
          >
            {loading ? (
              <Box pt={3} display="flex" width="100%" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Column header="Top Products" handleLinkClick={onClose}>
                  {isDefault ? (
                    <ProductCategories categories={productCategories} handleLinkClick={onClose} />
                  ) : (
                    <Products
                      products={products}
                      viewAllUrl={viewAllUrl}
                      handleLinkClick={onClose}
                    />
                  )}
                </Column>

                <Column header="Top Resources" handleLinkClick={onClose}>
                  <Resources
                    resources={resources}
                    viewAllUrl={viewAllUrl}
                    handleLinkClick={onClose}
                  />
                </Column>

                <Box
                  pt={3}
                  display="flex"
                  borderTop="1px solid"
                  borderColor="gray.300"
                  width="100%"
                  justifyContent="right"
                >
                  <Box>
                    <Link
                      aria-label={`View all search results`}
                      fontSize="0.875rem"
                      href={viewAllUrl}
                      onClick={onClose}
                      sx={{ textDecoration: 'none' }}
                      textTransform="uppercase"
                    >
                      View All
                    </Link>
                  </Box>
                </Box>
              </>
            )}
          </Grid>
        </Container>
      </Box>
    );
  },
);

SearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

// needed for arrow function declaration with forwardRef
// https://stackoverflow.com/questions/59716140/using-forwardref-with-proptypes-and-eslint
SearchModal.displayName = 'SearchModal';

export default SearchModal;
